import { format } from 'date-fns';
import type { FinanceAccount, FinanceTransaction } from '@/types/finance';
import type { InvoiceCandidate } from './types';

interface MatchInput {
  /** Valor da linha do OFX (despesa na conta de origem). */
  amount: number;
  /** Data da linha do OFX. */
  date: Date;
  /** Conta de destino do import — onde o débito da fatura aparece. */
  paymentAccountId: string;
}

interface Context {
  cards: FinanceAccount[]; // Apenas type === 'credit_card'
  transactions: FinanceTransaction[]; // Pendentes na conta-cartão
}

const CENTS = 100;
const eqAmount = (a: number, b: number) => Math.round(a * CENTS) === Math.round(b * CENTS);

/**
 * Para cada linha de import, busca cartões cuja soma de transações pendentes
 * em algum período de 35 dias termina em torno da data do débito iguale o valor da linha.
 *
 * Estratégia: para cada cartão, ordena transações pendentes por data,
 * para cada janela "do início até o dia X" agrupada por mês, soma e compara.
 * Suficiente para o caso comum (1 fatura por cartão por mês).
 */
export function findInvoiceCandidates(input: MatchInput, ctx: Context): InvoiceCandidate[] {
  const candidates: InvoiceCandidate[] = [];

  for (const card of ctx.cards) {
    const cardPending = ctx.transactions
      .filter((t) => t.accountId === card.id && t.status === 'pending')
      .sort((a, b) => a.dueDate.getTime() - b.dueDate.getTime());

    if (cardPending.length === 0) continue;

    // Agrupa por (ano, mês) — heurística de fatura mensal.
    const byMonth = new Map<string, FinanceTransaction[]>();
    for (const t of cardPending) {
      const key = format(t.dueDate, 'yyyy-MM');
      const arr = byMonth.get(key) ?? [];
      arr.push(t);
      byMonth.set(key, arr);
    }

    for (const [, txs] of byMonth) {
      const total = txs.reduce((s, t) => s + Number(t.amount), 0);
      if (eqAmount(total, input.amount)) {
        const first = txs[0]!.dueDate;
        const last = txs[txs.length - 1]!.dueDate;
        candidates.push({
          cardAccountId: card.id,
          cardAccountName: card.name,
          periodStart: format(first, 'yyyy-MM-dd'),
          periodEnd: format(last, 'yyyy-MM-dd'),
          total,
          transactionIds: txs.map((t) => t.id),
        });
      }
    }
  }

  return candidates;
}
